import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { elevationProfile, type Geometry, type ProfilePoint } from "../lib/geo";
import { formatDistance, formatElapsed, formatElevation } from "../lib/format";
import { useT } from "../lib/i18n";

// GPS elevation is noisy; summing every positive delta overstates ascent.
// An 8 m hysteresis (only count a rise once it exceeds the threshold from the
// last local low) lands within ~2% of the API's precomputed track.ascentM on
// seeded data.
const ASCENT_HYSTERESIS_M = 8;

function smoothedAscent(points: ProfilePoint[]): number {
  let ascent = 0;
  let base = points[0]!.z;
  for (const p of points) {
    if (p.z < base) base = p.z;
    else if (p.z - base >= ASCENT_HYSTERESIS_M) {
      ascent += p.z - base;
      base = p.z;
    }
  }
  return ascent;
}

const HEIGHT = 180;
const PAD = { top: 12, right: 10, bottom: 20, left: 44 };

// Real pixel width via ResizeObserver so the viewBox matches the rendered
// size 1:1 — scaling the SVG instead would distort the axis text.
function useMeasuredWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(720);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      const w = entry?.contentRect.width;
      if (w) setWidth(Math.round(w));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { ref, width };
}

type XAxis = "distance" | "time";

/**
 * Distance-vs-elevation chart for a track geometry that carries Z coordinates.
 * Pass the RAW geometry from the API (before cleanGeometry strips Z). Renders
 * nothing when there is no real elevation data.
 *
 * When `pointTimes` (ISO timestamps aligned 1:1 with the geometry points) is
 * given and lines up with the profile points, a Distance | Time toggle appears
 * and the X axis can switch to elapsed time.
 *
 * `onHoverPoint` receives the [lng, lat] of the hovered track point (null when
 * the pointer leaves) so a map marker can be synced later; it works the same
 * in both axis modes.
 */
export function ElevationProfile({
  geom,
  pointTimes,
  onHoverPoint,
  className,
}: {
  geom: Geometry | null | undefined;
  pointTimes?: string[] | null;
  onHoverPoint?: (pt: [number, number] | null) => void;
  className?: string;
}) {
  const { t } = useT();
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [axisChoice, setAxisChoice] = useState<XAxis>("distance");
  const { ref: boxRef, width } = useMeasuredWidth();

  const base = useMemo(() => {
    const points = elevationProfile(geom);
    if (!points) return null;
    const totalD = points[points.length - 1]!.d;
    if (totalD <= 0) return null;
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (const p of points) {
      if (p.z < minZ) minZ = p.z;
      if (p.z > maxZ) maxZ = p.z;
    }
    // Elapsed seconds per profile point — only when the timestamps align 1:1
    // with the points and are monotonic; otherwise the time axis is offered
    // not at all rather than wrongly.
    let elapsed: number[] | null = null;
    if (pointTimes && pointTimes.length === points.length) {
      const t0 = Date.parse(pointTimes[0]!);
      elapsed = pointTimes.map((t) => (Date.parse(t) - t0) / 1000);
      for (let i = 0; i < elapsed.length; i++) {
        if (!Number.isFinite(elapsed[i]!) || (i > 0 && elapsed[i]! < elapsed[i - 1]!)) {
          elapsed = null;
          break;
        }
      }
      if (elapsed && elapsed[elapsed.length - 1]! <= 0) elapsed = null;
    }
    return { points, totalD, minZ, maxZ, ascent: smoothedAscent(points), elapsed };
  }, [geom, pointTimes]);

  const axis: XAxis = base?.elapsed && axisChoice === "time" ? "time" : "distance";

  const chart = useMemo(() => {
    if (!base) return null;
    const { points, totalD, minZ, maxZ, elapsed } = base;
    const xs = axis === "time" && elapsed ? elapsed : points.map((p) => p.d);
    const totalX = xs[xs.length - 1]!;
    // Pad the Y domain so a flat track doesn't collapse to a line on the edge.
    const span = Math.max(maxZ - minZ, 20);
    const y0 = minZ - span * 0.08;
    const y1 = maxZ + span * 0.08;
    const px = (x: number) => PAD.left + (x / totalX) * (width - PAD.left - PAD.right);
    const py = (z: number) => PAD.top + (1 - (z - y0) / (y1 - y0)) * (HEIGHT - PAD.top - PAD.bottom);
    const line = points.map((p, i) => `${i === 0 ? "M" : "L"}${px(xs[i]!).toFixed(1)} ${py(p.z).toFixed(1)}`).join("");
    const baseY = HEIGHT - PAD.bottom;
    const area = `${line}L${px(totalX).toFixed(1)} ${baseY}L${PAD.left} ${baseY}Z`;
    return { xs, totalX, totalD, px, py, line, area };
  }, [base, axis, width]);

  if (!base || !chart) return null;
  const { points, totalD, minZ, maxZ, ascent, elapsed } = base;
  const { xs, totalX, px, py, line, area } = chart;

  const formatX = axis === "time" ? formatElapsed : formatDistance;

  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const xPix = ((e.clientX - rect.left) / rect.width) * width;
    const x = ((xPix - PAD.left) / (width - PAD.left - PAD.right)) * totalX;
    // Binary search for the nearest point along the active X axis (both axes
    // are monotonic non-decreasing).
    let lo = 0;
    let hi = xs.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (xs[mid]! < x) lo = mid + 1;
      else hi = mid;
    }
    const idx = lo > 0 && x - xs[lo - 1]! < xs[lo]! - x ? lo - 1 : lo;
    setHoverIdx(idx);
    onHoverPoint?.([points[idx]!.lng, points[idx]!.lat]);
  };
  const onLeave = () => {
    setHoverIdx(null);
    onHoverPoint?.(null);
  };

  const hover = hoverIdx != null && hoverIdx < points.length ? points[hoverIdx]! : null;
  const hoverX = hoverIdx != null && hoverIdx < xs.length ? px(xs[hoverIdx]!) : 0;
  const tooltipOnLeft = hoverX > width * 0.72;

  return (
    <div ref={boxRef} className={className}>
      {elapsed && (
        <div className="mb-1 flex justify-end gap-1" role="group" aria-label={t("chart.axisAria")}>
          {(["distance", "time"] as const).map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => setAxisChoice(a)}
              aria-pressed={axis === a}
              className={`rounded-md px-2 py-0.5 text-xs font-semibold transition-colors ${
                axis === a ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"
              }`}
            >
              {a === "distance" ? t("stat.distance") : t("stat.time")}
            </button>
          ))}
        </div>
      )}
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${HEIGHT}`}
        width={width}
        height={HEIGHT}
        className="block max-w-full"
        role="img"
        aria-label={t("chart.aria", { distance: formatDistance(totalD), min: formatElevation(minZ), max: formatElevation(maxZ) })}
        onPointerMove={onMove}
        onPointerLeave={onLeave}
      >
        {/* recessive horizontal grid at min / max elevation */}
        {[minZ, maxZ].map((z) => (
          <g key={z}>
            <line
              x1={PAD.left}
              x2={width - PAD.right}
              y1={py(z)}
              y2={py(z)}
              className="stroke-border"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
            <text
              x={PAD.left - 6}
              y={py(z) + 3.5}
              textAnchor="end"
              fontSize={10}
              className="fill-muted-foreground"
            >
              {Math.round(z)} m
            </text>
          </g>
        ))}

        <path d={area} className="fill-primary/15" />
        <path d={line} fill="none" className="stroke-primary" strokeWidth={2} strokeLinejoin="round" />
        <line
          x1={PAD.left}
          x2={width - PAD.right}
          y1={HEIGHT - PAD.bottom}
          y2={HEIGHT - PAD.bottom}
          className="stroke-border"
          strokeWidth={1}
        />

        {/* X-axis ticks (distance or elapsed time) */}
        {[0, totalX / 2, totalX].map((x, i) => (
          <text
            key={i}
            x={px(x)}
            y={HEIGHT - 6}
            textAnchor={i === 0 ? "start" : i === 2 ? "end" : "middle"}
            fontSize={10}
            className="fill-muted-foreground"
          >
            {formatX(x)}
          </text>
        ))}

        {hover && (
          <g pointerEvents="none">
            <line
              x1={hoverX}
              x2={hoverX}
              y1={PAD.top}
              y2={HEIGHT - PAD.bottom}
              className="stroke-muted-foreground"
              strokeWidth={1}
            />
            <circle
              cx={hoverX}
              cy={py(hover.z)}
              r={4}
              className="fill-primary stroke-background"
              strokeWidth={2}
            />
            <g transform={`translate(${tooltipOnLeft ? hoverX - 118 : hoverX + 8}, ${PAD.top})`}>
              <rect width={110} height={20} rx={4} fill="#0f172a" opacity={0.85} />
              <text x={55} y={13.5} textAnchor="middle" fontSize={10.5} fill="#ffffff">
                {formatX(xs[hoverIdx!]!)} · {formatElevation(hover.z)}
              </text>
            </g>
          </g>
        )}
      </svg>

      <dl className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <div>
          <dt className="inline">{t("stat.distance")} </dt>
          <dd className="inline font-semibold text-foreground">{formatDistance(totalD)}</dd>
        </div>
        <div>
          <dt className="inline">{t("stat.ascent")} </dt>
          <dd className="inline font-semibold text-foreground">{formatElevation(ascent)}</dd>
        </div>
        <div>
          <dt className="inline">{t("chart.min")} </dt>
          <dd className="inline font-semibold text-foreground">{formatElevation(minZ)}</dd>
        </div>
        <div>
          <dt className="inline">{t("chart.max")} </dt>
          <dd className="inline font-semibold text-foreground">{formatElevation(maxZ)}</dd>
        </div>
      </dl>
    </div>
  );
}
